import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ArrowForwardRounded,
  AutoAwesomeRounded,
  ChevronRightRounded,
  DescriptionOutlined,
  EventOutlined,
  FolderOpenOutlined,
  GavelOutlined,
  GroupsOutlined,
  QueryStatsRounded,
  SearchRounded,
  TrendingUpRounded,
  AccessTimeRounded,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import FloatingChatWidget from '../components/FloatingChatWidget';
import { loadBookmarkData, loadBookmarks } from '../utils/caseExplorerStorage';
import { firmDisplayName, firmProfile } from '../edition';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const quickActions = [
  { label: 'New client intake', description: 'Open a matter in minutes', icon: <AddRounded />, path: '/intake', color: '#0D9488' },
  { label: 'Research a case', description: 'Search and read Canadian decisions', icon: <SearchRounded />, path: '/research?view=decisions', color: '#245B88' },
  { label: 'Generate a draft', description: 'Start from a legal template', icon: <DescriptionOutlined />, path: '/drafting', color: '#6D4FB3' },
  { label: 'Build a playbook', description: 'Turn facts into a strategy', icon: <AutoAwesomeRounded />, path: '/playbooks', color: '#C45F21' },
];

const darkToneMap = {
  '#245B88': '#69AFE0',
  '#C2414B': '#FF7A86',
  '#6D4FB3': '#B794F4',
  '#0D9488': '#42C7BA',
  '#D97706': '#F3AE55',
  '#16855B': '#4DCC94',
  '#C45F21': '#F29A61',
};

const accessibleTone = (theme, color) => theme.palette.mode === 'dark' ? (darkToneMap[color] || color) : color;

function MetricCard({ label, value, helper, icon, color, trend }) {
  return (
    <Card sx={{ p: 2.25, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography sx={{ fontSize: 27, fontWeight: 780, lineHeight: 1.25, mt: 0.35 }}>{value}</Typography>
        </Box>
        <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 2.5, bgcolor: theme => `${accessibleTone(theme, color)}1F`, color: theme => accessibleTone(theme, color) }}>{icon}</Box>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mt: 1.5 }}>
        {trend && <TrendingUpRounded sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" color={trend ? 'success.main' : 'text.secondary'}>{helper}</Typography>
      </Stack>
    </Card>
  );
}

function SectionCard({ title, subtitle, actionPath, children, sx }) {
  return (
    <Card sx={{ minWidth: 0, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 2, sm: 2.5 }, pt: 2.25, pb: 1.75 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {actionPath && <Button component={RouterLink} to={actionPath} size="small" endIcon={<ArrowForwardRounded />} sx={{ whiteSpace: 'nowrap' }}>See All</Button>}
      </Stack>
      <Divider />
      {children}
    </Card>
  );
}

export default function DashboardMain() {
  const navigate = useNavigate();
  const [health, setHealth] = useState({ loading: true, online: false, aiConfigured: false });
  const [workspace, setWorkspace] = useState({
    matters: [],
    team: [],
    summary: { activeMatters: 0, deadlinesNextWeek: 0, awaitingApproval: 0, evidenceFiles: 0 },
  });
  const [firstName, setFirstName] = useState('there');
  const [firm, setFirm] = useState(null);
  const bookmarks = useMemo(() => loadBookmarks(), []);
  const bookmarkData = useMemo(() => loadBookmarkData(), []);
  const savedAuthorities = bookmarks.map(id => bookmarkData[id]).filter(Boolean).slice(0, 3);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/health`).then(response => { if (!response.ok) throw new Error('API unavailable'); return response.json(); }),
      fetch(`${API_BASE}/api/matters/summary`).then(response => { if (!response.ok) throw new Error('Matter summary unavailable'); return response.json(); }),
      fetch(`${API_BASE}/api/matters`).then(response => { if (!response.ok) throw new Error('Matters unavailable'); return response.json(); }),
      fetch(`${API_BASE}/api/administration/team`).then(response => { if (!response.ok) throw new Error('Team unavailable'); return response.json(); }),
      fetch(`${API_BASE}/api/auth/me`).then(response => { if (!response.ok) throw new Error('Account unavailable'); return response.json(); }),
    ])
      .then(([healthData, summary, matterData, teamData, me]) => {
        setHealth({ loading: false, online: true, aiConfigured: Boolean(healthData.aiConfigured) });
        setWorkspace({ summary, matters: matterData.matters || [], team: teamData.team || [] });
        setFirstName(String(me.user?.name || 'there').split(/\s+/)[0]);
        setFirm(me.user || null);
      })
      .catch(() => setHealth({ loading: false, online: false, aiConfigured: false }));
  }, []);

  const matters = workspace.matters.slice(0, 4).map(item => {
    const clientInitials = (item.clientName || item.title || 'M').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
    const progress = item.counts?.tasks ? Math.round((item.counts.completedTasks / item.counts.tasks) * 100) : 0;
    return {
      id: item.id,
      name: item.title,
      detail: `${item.clientName || 'Client not set'} · ${item.court || item.jurisdiction || 'Court not set'}`,
      next: item.nextCourtDate ? `Court ${new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(new Date(`${item.nextCourtDate}T12:00:00`))}` : 'No court date',
      stage: item.stage?.replaceAll('-', ' ') || 'intake',
      tone: item.nextCourtDate ? 'warning' : 'info',
      progress,
      initials: clientInitials,
    };
  });

  const schedule = workspace.matters
    .filter(item => item.nextCourtDate)
    .sort((a, b) => a.nextCourtDate.localeCompare(b.nextCourtDate))
    .slice(0, 3)
    .map(item => ({
      time: new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(new Date(`${item.nextCourtDate}T12:00:00`)),
      period: '',
      title: item.title,
      detail: `${item.court || item.jurisdiction || 'Court not set'} · ${item.clientName || 'Client not set'}`,
      color: '#245B88',
      id: item.id,
    }));

  const today = new Intl.DateTimeFormat('en-CA', { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date());
  const firmName = firm?.organizationName || firmProfile.legalName;
  const workspaceName = firmDisplayName(firmName);
  const mattersWithoutDates = workspace.matters.filter(item => !item.nextCourtDate).length;
  const workQueue = [
    { label: 'Approvals to review', value: workspace.summary.awaitingApproval, detail: 'Completed work waiting for a human decision', path: '/playbooks', icon: <DescriptionOutlined />, color: '#6D4FB3' },
    { label: 'Deadlines this week', value: workspace.summary.deadlinesNextWeek, detail: 'Verified dates requiring attention', path: '/matters', icon: <EventOutlined />, color: '#C2414B' },
    { label: 'Matters missing a court date', value: mattersWithoutDates, detail: 'Confirm whether a date applies', path: '/matters', icon: <GavelOutlined />, color: '#D97706' },
    { label: 'Saved authorities', value: bookmarks.length, detail: 'Research ready to use or verify', path: '/research?view=decisions', icon: <QueryStatsRounded />, color: '#0D9488' },
  ];

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', pb: 4 }}>
      <Box className="rise-in" sx={{ mb: 2.5, display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7 }}>
            <Typography variant="overline" color="secondary.main">{today}</Typography>
            <Chip label={health.loading ? 'Checking workspace' : health.online ? 'Workspace online' : 'API offline'} size="small" color={health.online ? 'success' : 'warning'} variant="outlined" sx={{ height: 24, fontSize: 10 }} />
          </Stack>
          <Typography variant="h3">Good morning, {firstName}.</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>{firm ? `${firmName} · ${firm.professionalTitle || 'Firm member'}` : `${firmProfile.legalName} · ${firmProfile.licence}`}</Typography>
        </Box>
        <Stack direction="row" spacing={1.25} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button variant="outlined" startIcon={<AccessTimeRounded />} onClick={() => navigate('/practice?newTime=1')} sx={{ flex: { xs: 1, md: 'initial' } }}>Log time</Button>
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => navigate('/intake')} sx={{ flex: { xs: 1, md: 'initial' } }}>New matter</Button>
        </Stack>
      </Box>

      {!health.loading && health.online && workspace.matters.length === 0 && (
        <Card sx={{ p: { xs: 2, md: 2.5 }, mb: 2, border: '1px solid', borderColor: 'primary.light', bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(120, 175, 213, 0.08)' : '#F5FAFD' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems={{ lg: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="secondary.main">START HERE</Typography>
              <Typography variant="h5">Set up {workspaceName} in three clear steps</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>The private firm workspace is ready. Invite the team, complete a conflict-first intake, then work from the matter lifecycle.</Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" onClick={() => navigate('/practice')}>1. Invite team</Button>
              <Button variant="contained" onClick={() => navigate('/intake')}>2. Open first matter</Button>
              <Button variant="outlined" onClick={() => navigate('/playbooks')}>3. Follow lifecycle</Button>
            </Stack>
          </Stack>
        </Card>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: 1.4, sm: 2 }, mb: 2 }}>
        <MetricCard label="Active matters" value={workspace.summary.activeMatters} helper={`${workspace.summary.evidenceFiles} evidence files preserved`} icon={<GavelOutlined />} color="#245B88" trend={workspace.summary.activeMatters > 0} />
        <MetricCard label="Deadlines this week" value={workspace.summary.deadlinesNextWeek} helper="Only verified dates are counted" icon={<EventOutlined />} color="#C2414B" />
        <MetricCard label="Awaiting approval" value={workspace.summary.awaitingApproval} helper="Human review gates" icon={<DescriptionOutlined />} color="#6D4FB3" />
        <MetricCard label="Authorities saved" value={bookmarks.length} helper={bookmarks.length ? 'In your verified research library' : 'No authorities saved yet'} icon={<QueryStatsRounded />} color="#0D9488" trend={bookmarks.length > 0} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.65fr) minmax(320px, .75fr)' }, gap: 2, alignItems: 'start' }}>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <SectionCard title="Priority matters" subtitle="Your active private matter files" actionPath="/matters">
          <Box>
            {matters.length ? matters.map((matter, index) => (
              <Box key={matter.id} onClick={() => navigate(`/matters?id=${encodeURIComponent(matter.id)}`)} sx={{ px: { xs: 1.75, sm: 2.5 }, py: 1.8, cursor: 'pointer', borderBottom: index < matters.length - 1 ? '1px solid' : 0, borderColor: 'divider', transition: 'background-color .2s', '&:hover': { bgcolor: 'action.hover' } }}>
                <Stack direction="row" alignItems="center" spacing={{ xs: 1.25, sm: 1.75 }}>
                  <Avatar sx={{ width: 39, height: 39, bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(105, 175, 224, .16)' : '#E7F0F7', color: theme => accessibleTone(theme, '#245B88'), fontSize: 12, fontWeight: 800 }}>{matter.initials}</Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.8}>
                      <Typography sx={{ fontSize: 14, fontWeight: 740 }}>{matter.name}</Typography>
                      <Chip label={matter.stage} color={matter.tone} size="small" variant="outlined" sx={{ height: 22, fontSize: 10 }} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap>{matter.detail}</Typography>
                  </Box>
                  <Box sx={{ minWidth: { xs: 72, sm: 140 }, textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: matter.tone === 'error' ? 'error.main' : 'text.secondary' }}>{matter.next}</Typography>
                    <LinearProgress variant="determinate" value={matter.progress} sx={{ mt: 0.8, ml: 'auto', maxWidth: 95, height: 4, borderRadius: 5, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { bgcolor: matter.tone === 'error' ? 'error.main' : matter.tone === 'warning' ? 'warning.main' : 'secondary.main' } }} />
                  </Box>
                  <ChevronRightRounded sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'block' } }} />
                </Stack>
              </Box>
            )) : <Box sx={{ p: 4, textAlign: 'center' }}><FolderOpenOutlined sx={{ color: 'text.disabled', fontSize: 34 }} /><Typography sx={{ mt: 1, fontWeight: 700 }}>No private matters yet</Typography><Typography variant="caption" color="text.secondary">Create a matter to start the evidence, facts, research, and workflow record.</Typography><br /><Button onClick={() => navigate('/matters')} size="small" sx={{ mt: 1 }}>Create first matter</Button></Box>}
          </Box>
          </SectionCard>

          <SectionCard title="Start something" subtitle="Move from question to work product">
          <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25 }}>
            {quickActions.map(action => (
              <Button key={action.label} onClick={() => navigate(action.path)} variant="outlined" sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1.6, minHeight: 74, bgcolor: 'background.paper' }}>
                <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: `${action.color}12`, color: action.color, mr: 1.35, flexShrink: 0 }}>{action.icon}</Box>
                <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 13, fontWeight: 750, color: 'text.primary' }}>{action.label}</Typography><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{action.description}</Typography></Box>
              </Button>
            ))}
          </Box>
          </SectionCard>

          <SectionCard title="Your firm team" subtitle={`${workspace.team.length} active or invited member${workspace.team.length === 1 ? '' : 's'}`} actionPath="/practice">
          <Box sx={{ px: 2.25, py: 1.1 }}>
            {workspace.team.slice(0, 5).map((person, index) => (
              <Stack key={person.id} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1.15, borderBottom: index < Math.min(workspace.team.length, 5) - 1 ? '1px solid' : 0, borderColor: 'divider' }}>
                <Avatar sx={{ width: 34, height: 34, fontSize: 11, bgcolor: person.status === 'invited' ? 'grey.300' : 'primary.main' }}>{String(person.name || person.email).split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase()}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontSize: 13.5, fontWeight: 720 }} noWrap>{person.name}</Typography><Typography variant="caption" color="text.secondary">{person.professionalTitle || person.role}</Typography></Box>
                {person.status === 'invited' && <Chip label="Invited" size="small" variant="outlined" />}
              </Stack>
            ))}
            {!workspace.team.length && <Box sx={{ py: 2.5, textAlign: 'center' }}><GroupsOutlined sx={{ color: 'text.disabled' }} /><Typography variant="body2" color="text.secondary" sx={{ mt: 0.7 }}>Invite the people who work in this firm.</Typography></Box>}
          </Box>
          </SectionCard>

          <SectionCard title="Saved authorities" subtitle={savedAuthorities.length ? 'Recently bookmarked research' : 'Build your research library'} actionPath="/research?view=decisions">
          <Box sx={{ p: 2.25 }}>
            {savedAuthorities.length ? savedAuthorities.map(item => (
              <Stack component={RouterLink} to={item.source === 'hosted-case-archive' ? `/research?view=decisions&case=${encodeURIComponent(item.id || '')}&title=${encodeURIComponent(item.name || '')}&citation=${encodeURIComponent(item.citation || '')}` : `/research?view=decisions&q=${encodeURIComponent(item.citation || item.name || '')}`} key={item.id || item.citation || item.name} direction="row" alignItems="center" spacing={1.2} sx={{ py: 1, color: 'inherit', textDecoration: 'none', borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}><GavelOutlined sx={{ fontSize: 19, color: 'primary.light' }} /><Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>{item.name}</Typography><Typography variant="caption" color="text.secondary">{item.citation || 'Saved case'}</Typography></Box><ChevronRightRounded sx={{ fontSize: 18, color: 'text.disabled' }} /></Stack>
            )) : (
              <Box sx={{ py: 2.5, textAlign: 'center' }}><SearchRounded sx={{ fontSize: 34, color: 'text.disabled' }} /><Typography sx={{ mt: 1, fontSize: 13.5, fontWeight: 700 }}>No saved authorities yet</Typography><Typography variant="caption" color="text.secondary">Bookmark a case to keep it close to your work.</Typography><br /><Button component={RouterLink} to="/research?view=decisions" size="small" sx={{ mt: 1 }}>Start researching</Button></Box>
            )}
          </Box>
          </SectionCard>
        </Stack>

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <SectionCard title="Upcoming court dates" subtitle="Verified dates from active matters" actionPath="/matters">
            <Box sx={{ px: 2.25, py: 1.2 }}>
              {schedule.length ? schedule.map((item, index) => (
                <Stack key={item.id} direction="row" spacing={1.5} onClick={() => navigate(`/matters?id=${encodeURIComponent(item.id)}`)} sx={{ py: 1.45, cursor: 'pointer', borderBottom: index < schedule.length - 1 ? '1px solid' : 0, borderColor: 'divider' }}>
                  <Box sx={{ width: 48, flexShrink: 0 }}><Typography sx={{ fontSize: 13, fontWeight: 780 }}>{item.time}</Typography><Typography sx={{ fontSize: 9.5, fontWeight: 800, color: 'text.disabled' }}>{item.period}</Typography></Box>
                  <Box sx={{ width: 3, borderRadius: 4, bgcolor: theme => accessibleTone(theme, item.color) }} />
                  <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 13.5, fontWeight: 720 }}>{item.title}</Typography><Typography variant="caption" color="text.secondary">{item.detail}</Typography></Box>
                </Stack>
              )) : <Box sx={{ py: 3, textAlign: 'center' }}><EventOutlined sx={{ color: 'text.disabled' }} /><Typography variant="body2" color="text.secondary" sx={{ mt: 0.7 }}>No verified upcoming court dates.</Typography></Box>}
            </Box>
          </SectionCard>

          <SectionCard title="Workspace intelligence" subtitle="AI and knowledge readiness">
            <Box sx={{ p: 2.4 }}>
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: theme => theme.palette.mode === 'dark' ? (health.aiConfigured ? 'rgba(76, 203, 147, .14)' : 'rgba(244, 180, 81, .14)') : (health.aiConfigured ? '#E1F6EC' : '#FFF3DA'), color: health.aiConfigured ? 'success.main' : 'warning.main' }}>
                  {health.loading ? <CircularProgress size={22} /> : <AutoAwesomeRounded />}
                </Box>
                <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 13.5, fontWeight: 750 }}>{health.aiConfigured ? 'AI workspace is ready' : 'Connect AI features'}</Typography><Typography variant="caption" color="text.secondary">{health.aiConfigured ? 'Copilot, drafting, and research are online.' : 'Add an API key to enable copilot and drafting.'}</Typography></Box>
              </Stack>
              <Button fullWidth variant={health.aiConfigured ? 'outlined' : 'contained'} onClick={() => navigate('/settings')} sx={{ mt: 2 }}>{health.aiConfigured ? 'Review configuration' : 'Open setup guide'}</Button>
            </Box>
          </SectionCard>

          <SectionCard title="Work queue" subtitle="What needs attention next">
            <Box sx={{ px: 1.2, py: 0.8 }}>
              {workQueue.map((item, index) => (
                <Button key={item.label} fullWidth onClick={() => navigate(item.path)} sx={{ px: 1.1, py: 1.25, justifyContent: 'flex-start', textAlign: 'left', color: 'text.primary', borderRadius: 2, borderBottom: index < workQueue.length - 1 ? '1px solid' : 0, borderColor: 'divider' }}>
                  <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: theme => `${accessibleTone(theme, item.color)}1F`, color: theme => accessibleTone(theme, item.color), mr: 1.25, '& svg': { fontSize: 19 } }}>{item.icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontSize: 13, fontWeight: 730 }}>{item.label}</Typography><Typography variant="caption" color="text.secondary">{item.detail}</Typography></Box>
                  <Typography sx={{ ml: 1, fontSize: 22, fontWeight: 800, color: item.value ? 'primary.main' : 'text.disabled' }}>{item.value}</Typography>
                  <ChevronRightRounded sx={{ ml: 0.5, color: 'text.disabled' }} />
                </Button>
              ))}
            </Box>
          </SectionCard>
        </Stack>
      </Box>

      <FloatingChatWidget maxMessages={4} />
    </Box>
  );
}
